import { AppContext } from "@/App";
import { Button } from "@/Components/ui/button"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from "@/Components/ui/dropdown-menu"
import React from "react";
import { useNavigate } from "react-router-dom";
import DeleteButton from "./ui/delete-button";
import { ChevronDown } from "lucide-react";
import { DownloadIcon } from "./ui/Icons/TooltipIcon"
import { useResponsiveZoom } from "@/hooks/use-responsive-zoom";
import { useIsMobile } from "@/hooks/use-mobile";
// import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
// import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
// import { projectTypes } from "@/utils/GlobalConstants";

export function ProjectSelect() {

    const app = React.useContext(AppContext);
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = React.useState(false);
    const newProjectType = 1;
    // const [newProjectType, setNewProjectType] = React.useState<number>(1);

    // const [dialogOpen, setDialogOpen] = React.useState(false);
    // const [dialogTitle, setDialogTitle] = React.useState("");
    // const [dialogContent, setDialogContent] = React.useState<React.ReactNode>(null);

    const zoomLevel = useResponsiveZoom();
    const isMobile = useIsMobile();

    // close the dropdown menu only once the app.selectedProject has been set
    React.useEffect(() => {
        if (app.selectedProject) {
            setIsOpen(false);
        }
    }, [app.selectedProject]);

    async function AddNewProject() {
        // add new project
        document.body.style.cursor = 'wait';
        fetch("Projects/" + newProjectType, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                name: "New Project",
            }),
        })
            .then((response) => response.json())
            .then((data) => {
                const newProject = { id: data.projectId, name: data.projectName, projectTypeId: data.projectTypeId };
                app.setProjects((prevProjects) => [...prevProjects, newProject]);
                app.setSelectedProject(newProject);
                app.showActionNotification("Success", "New project added", "success");
                navigate(`/Charter`, { state: { autoLock: true } });
            })
            .catch(() => {
                app.showActionNotification("Error", "Error adding new project", "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    function DeleteProject(projectId: number) {
        // check if this is the last project
        if (app.projects.length === 1) {
            app.showActionNotification("Action Disallowed", "Cannot Delete - At least one project is required", "default");
            return;
        }
        // delete project
        document.body.style.cursor = 'wait';
        fetch(`Projects/${projectId}`, {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
            },
        }
        )
            .then((response) => {
                if (response.ok) {
                    app.setProjects(app.projects.filter((project) => project.id !== projectId));
                    app.showActionNotification("Success", "Project deleted", "success");
                    app.setSelectedProject(app.projects[0]);
                }
                else {
                    app.showActionNotification("Error", "Error deleting project", "destructive");
                }

            })
            .catch(() => {
                app.showActionNotification("Error", "Error deleting project", "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    function handleNewProject() {
        // Leave this in as we will probably want to add more options later

        // setDialogTitle("Add New Project");
        // setDialogContent(
        //     <div className="flex flex-col">
        //         <label className="text-sm mb-2">Select Project Type:</label>
        //         <Select
        //             defaultValue={newProjectType.toString()}
        //             onValueChange={(value) => setNewProjectType(Number(value))}
        //         >
        //             <SelectTrigger>
        //                 <SelectValue placeholder="Project Type..." />
        //             </SelectTrigger>
        //             <SelectContent>
        //                 <SelectItem value={"1"}>{projectTypes.find(x => x.typeId === 1)?.typeName}</SelectItem>
        //                 <SelectItem value={"2"}>{projectTypes.find(x => x.typeId === 2)?.typeName}</SelectItem>
        //             </SelectContent>
        //         </Select>
        //         <div className="flex flex-row justify-end space-x-4">
        //             <Button onClick={() => { setDialogOpen(false); }} className="mt-2">Cancel</Button>
        //             <Button onClick={() => { AddNewProject(); setDialogOpen(false); }} className="mt-2">Create Project</Button>
        //         </div>
        //     </div>
        // );
        // setDialogOpen(true);
        // setIsOpen(false);

        AddNewProject();
    }

    function handleImportProject() {
        // Create a hidden file input element
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = '.ppme';
        fileInput.style.display = 'none';

        fileInput.onchange = async (e: Event) => {
            const target = e.target as HTMLInputElement;
            const file = target.files?.[0];

            if (!file) {
                return;
            }

            // Check file extension
            if (!file.name.endsWith('.ppme')) {
                app.showActionNotification("Error", "Invalid file type. Please select a .ppme file", "destructive");
                return;
            }

            document.body.style.cursor = 'wait';

            try {
                // Read the file content
                const fileContent = await file.text();
                
                // Parse the JSON
                const projectData = JSON.parse(fileContent);

                // Send to the import API
                const response = await fetch("Projects/Import", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(projectData),
                });

                if (response.ok) {
                    const newProjectId = await response.json();
                    
                    // Fetch the new project details
                    const projectResponse = await fetch(`Projects/${newProjectId}`);
                    if (projectResponse.ok) {
                        const newProject = await projectResponse.json();
                        const projectInfo = { 
                            id: newProject.projectId, 
                            name: newProject.projectName, 
                            projectTypeId: newProject.projectTypeId 
                        };
                        
                        app.setProjects((prevProjects) => [...prevProjects, projectInfo]);
                        app.setSelectedProject(projectInfo);
                        app.showActionNotification("Success", "Project imported successfully", "success");
                        navigate(`/Charter`);
                    }
                } else {
                    const errorText = await response.text();
                    app.showActionNotification("Error", `Failed to import project: ${errorText}`, "destructive");
                }
            } catch (error) {
                if (error instanceof SyntaxError) {
                    app.showActionNotification("Error", "Invalid file format. File must contain valid JSON", "destructive");
                } else {
                    app.showActionNotification("Error", `Error importing project: ${error}`, "destructive");
                }
            } finally {
                document.body.style.cursor = 'default';
                // Clean up the file input
                document.body.removeChild(fileInput);
            }
        };

        // Append to body and trigger click
        document.body.appendChild(fileInput);
        fileInput.click();
        setIsOpen(false);
    }

    function handleExportProject(projectId: number) {
        document.body.style.cursor = 'wait';

        fetch(`Projects/Export/${projectId}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        })
            .then(async (response) => {
                if (response.ok) {
                    const projectData = await response.json();
                    
                    // Convert the JSON to a string
                    const jsonString = JSON.stringify(projectData, null, 2);
                    
                    // Create a Blob from the JSON string
                    const blob = new Blob([jsonString], { type: 'application/json' });
                    
                    // Create a download link
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    
                    // Use the project name for the filename, sanitized for file system
                    const projectName = app.projects.find(p => p.id === projectId)?.name || 'project';
                    const sanitizedName = projectName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
                    link.download = `${sanitizedName}_export.ppme`;
                    
                    // Trigger the download
                    document.body.appendChild(link);
                    link.click();
                    
                    // Clean up
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                    
                    app.showActionNotification("Success", "Project exported successfully", "success");
                } else {
                    const errorText = await response.text();
                    app.showActionNotification("Error", `Failed to export project: ${errorText}`, "destructive");
                }
            })
            .catch((error) => {
                app.showActionNotification("Error", `Error exporting project: ${error}`, "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
        
        setIsOpen(false);
    }

    // function handleMultiProjectDash() {
    //     navigate("/");
    //     setIsOpen(false);
    // }

    return (
        <>
            <DropdownMenu open={isOpen} onOpenChange={setIsOpen} modal={isMobile}>
                <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon" className={`bg-sidebar-background border-none w-full text-wrap font-normal shadow-none mx-auto pl-2 ${isMobile ? "text-base py-2" : ""}`}>
                        {app.selectedProject?.name ?? app.projects[0].name}
                        <ChevronDown
                            className={`ml-2 h-4 w-4 shrink-0 opacity-50 transition-transform duration-200 ${isOpen ? "rotate-180" : "rotate-0"}`}
                        />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center" style={isMobile ? {} : { zoom: zoomLevel }} className={isMobile ? "w-[90vw] max-h-[70vh] overflow-y-auto z-[9999]" : ""} sideOffset={5}>
                    {app.projects.map((project) => (
                        <DropdownMenuItem 
                            key={project.id} 
                            className={`flex flex-row justify-between ${isMobile ? "min-w-0 py-3 text-base" : "min-w-[275px]"}`} 
                            onSelect={(e) => e.preventDefault()}
                            onClick={() => app.setSelectedProject(project)}
                        >
                            <div className={`flex flex-row text-wrap ${isMobile ? "max-w-[70vw]" : "max-w-[200px]"}`}>{project.name}</div>
                            {app.authLevel === 5 && app.projects.length > 1 && (
                                <div className="flex flex-row" onClick={(e) => e.stopPropagation()}>
                                    <button onClick={() => handleExportProject(project.id)}>
                                        <DownloadIcon />
                                    </button>
                                    <DeleteButton
                                        action={() => DeleteProject(project.id)}
                                        title={"Delete Project?"}
                                        message={"This cannot be undone! Are you sure you want to delete " + project.name + "?"}
                                        tooltip={"Delete Project"}
                                        className={isMobile ? "mt-1 w-8 h-8" : "mt-1"}
                                        cancelled={(cancelled) => {
                                            if (cancelled) {
                                                setIsOpen(false);
                                                app.showActionNotification("Cancelled", "Project deletion cancelled", "default");
                                            }
                                        }}
                                    />
                                </div>
                            )}
                        </DropdownMenuItem>
                    ))}

                    {app.authLevel === 5 && (
                        <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleNewProject()} className={isMobile ? "py-3 text-base" : ""}>
                                Add New Project
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleImportProject()} className={isMobile ? "py-3 text-base" : ""}>
                                Import Project
                            </DropdownMenuItem>
                            {/* <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleMultiProjectDash()}>
                                Multi Project Dashboard
                            </DropdownMenuItem> */}
                        </>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>
            {/* <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent
                    className="flex flex-col min-w-fit"
                >
                    <DialogHeader>
                        <DialogTitle>{dialogTitle}</DialogTitle>
                    </DialogHeader>
                    {dialogContent}
                </DialogContent>
            </Dialog> */}
        </>
    )
}